// When the click lands under the overlay, `resolvePillPopoverClick` (if
// provided) is given the real element at that point — once the overlay is
// gone — so it can detect a variable pill and react the same way a direct
// click on an already-active editor would: a pill with a popover (value,
// date) or a repeatable pill (which opens the rows pop-up) gets SELECTED
// (PandaDoc two-click model — the next click on the selected pill is what
// opens the popover / the rows pop-up; see Froala.tsx). This is what makes a
// pill click do the right thing on the very first click, before the RichText
// element has been focused into edit mode (see Froala.tsx's `hasOverlay`).
const handleOverlayClick = (
  e: React.MouseEvent<HTMLSpanElement>,
  setHasOverlay: (hasOverlay: boolean) => void,
  resolvePillPopoverClick?: (target: HTMLElement | null) =>
    | "block"
    | "date"
    | "value"
    | "repeatable"
    | "value-date"
    // A pill with no popover — selected whole, so the caret-placement
    // fallback below must be skipped just like for the popover kinds.
    | "plain"
    | "none",
) => {
  setHasOverlay(false);
  setTimeout(() => {
    try {
      const elementAtPoint = document.elementFromPoint(e.clientX, e.clientY);
      if (!elementAtPoint) return;

      // Pill click: the popover is now open (or the click is a no-op read-only
      // block swallow) — skip the caret-placement fallback below entirely.
      if (resolvePillPopoverClick) {
        const result = resolvePillPopoverClick(elementAtPoint as HTMLElement);
        if (result !== "none") return;
      }

      // Find all text nodes in the editor
      const textNodes: Array<Text> = [];
      const walk = document.createTreeWalker(
        elementAtPoint,
        NodeFilter.SHOW_TEXT,
        null,
      );
      let node: Text | null;
      while ((node = walk.nextNode() as Text)) {
        textNodes.push(node);
      }

      // Find the text node closest to the click position
      let closestNode: Text | null = null;
      let minDistance = Number.POSITIVE_INFINITY;

      textNodes.forEach((textNode: Text) => {
        const range = document.createRange();
        range.selectNodeContents(textNode);
        const rect = range.getBoundingClientRect();

        // Skip if click is outside the text node's vertical bounds
        if (e.clientY < rect.top || e.clientY > rect.bottom) return;

        // Calculate distance to the text node
        const distance = Math.abs(e.clientX - rect.left);
        if (distance < minDistance) {
          minDistance = distance;
          closestNode = textNode;
        }
      });

      if (!closestNode) return;

      // Ensure closestNode is a Text node
      const textNode: Text = closestNode;

      // Binary search to find the closest character position in the selected text node
      const range = document.createRange();
      let start = 0;
      const nodeLength = textNode.textContent?.length || 0;
      let end = nodeLength - 1;
      let closestOffset = 0;
      minDistance = Number.POSITIVE_INFINITY;

      while (start <= end) {
        const mid = Math.floor((start + end) / 2);
        range.setStart(textNode, mid);
        range.setEnd(textNode, Math.min(mid + 1, nodeLength));
        const charRect = range.getBoundingClientRect();

        // Calculate distance from click to character
        const distance = Math.sqrt(
          Math.pow(e.clientX - (charRect.left + charRect.width / 2), 2) +
            Math.pow(e.clientY - (charRect.top + charRect.height / 2), 2),
        );

        if (distance < minDistance) {
          minDistance = distance;
          closestOffset = mid;
        }

        // Move search based on click position relative to character
        if (e.clientX < charRect.left) {
          end = mid - 1;
        } else {
          start = mid + 1;
        }
      }

      // Set selection to the closest character
      range.setStart(textNode, closestOffset);
      range.setEnd(textNode, closestOffset);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    } catch (error) {
      console.error(error);
    }
  }, 0);
};

export default handleOverlayClick;
